import { IonText } from '@ionic/react';
import { ReactNode } from 'react';
import { NumericInputPreferredUnits } from '../../../../../../../components/form/ion-form-numeric-input-lockable';
import { getLocalizedText } from '../../../../../../../i18n/i18n.utils';
import { ValidateStitchSettingsResult } from '../../../../../../../util/part.util';
import { SystemUnit } from '../../../../../shared/model/flow.types';
import { BeaconWeldingData } from '../../../model/beacon-welding-data';
import {
  convertStitchSystemValueToUom,
  getReadableValue,
} from './flow-item-stitch.utils';

interface StitchModificationNotificationProps {
  stitchSettings: BeaconWeldingData.StitchSettingsFormData;
  validatedSettingsResult: ValidateStitchSettingsResult;
  system: SystemUnit;
  preferredUnits: NumericInputPreferredUnits;
}

const StitchModifiedInfo = (
  props: StitchModificationNotificationProps
): ReactNode => {
  const { stitchSettings, validatedSettingsResult, system } = props;
  const { modifiedSettings } = validatedSettingsResult;

  const convertedStitchLength = convertStitchSystemValueToUom(
    modifiedSettings.lengthOfStitch,
    system
  );

  const convertedPitch = convertStitchSystemValueToUom(
    modifiedSettings.pitchOfStitch,
    system
  );

  const wrapperClasses = 'ion-padding-start ion-padding-end';

  if (stitchSettings.pitchLocked && stitchSettings.stitchLengthLocked) {
    return (
      <div className={wrapperClasses}>
        <p>
          {getLocalizedText(
            'common:stitch_settings_locked_fit_not_guaranteed_warning'
          )}
        </p>
      </div>
    );
  }

  const onlyPitchLocked =
    stitchSettings.pitchLocked && !stitchSettings.stitchLengthLocked;
  const isSamePitch = stitchSettings.pitch === convertedPitch;
  const isSameStitchLength =
    stitchSettings.stitchLength === convertedStitchLength;

  if (isSamePitch && isSameStitchLength) {
    return null;
  }

  const displayStitchLength = convertStitchSystemValueToUom(
    modifiedSettings.lengthOfStitch,
    system,
    3
  );

  const displayPitch = convertStitchSystemValueToUom(
    modifiedSettings.pitchOfStitch,
    system,
    3
  );

  const stitchLengthReadable = getReadableValue(displayStitchLength, system);
  const pitchReadable = getReadableValue(displayPitch, system);

  const stitchLengthMessage = !isSameStitchLength
    ? getLocalizedText('common:stitch_settings_modified_stitch_length', {
        length: stitchLengthReadable,
      })
    : undefined;

  const pitchMessage = !isSamePitch
    ? getLocalizedText('common:stitch_settings_modified_pitch', {
        pitch: pitchReadable,
      })
    : undefined;

  const modifiedSettingsMessage = stitchLengthMessage ?? pitchMessage;

  const adjustedMessage = onlyPitchLocked
    ? getLocalizedText(
        'common:stitch_settings_pitch_locked_fit_not_guaranteed_warning'
      )
    : getLocalizedText('common:stitch_settings_modified_to_fit_message');

  return (
    <div className={wrapperClasses}>
      <p>{adjustedMessage}</p>
      <p>
        <IonText color="primary">{modifiedSettingsMessage}</IonText>
      </p>
      <p>{getLocalizedText('common:stitch_settings_modified_unset')}</p>
    </div>
  );
};

export { StitchModifiedInfo };
